import { useState, useCallback, useEffect, useRef } from 'react'
import { getTaskStatus } from '../../../services/api/aiVideoGeneration'

const DEFAULT_POLL_INTERVAL = 2000
const SUCCESS_STATUSES = ['success', 'completed', 'done']

const extractDefaultTaskId = data => {
  if (!data || typeof data !== 'object') return null

  return (
    data['task-id'] ||
    data.task_id ||
    data.taskId ||
    data?.output?.['task-id'] ||
    data?.output?.task_id ||
    data?.output?.taskId ||
    ((data.status === 'pending' || data.status === 'processing') && data.id) ||
    null
  )
}

const extractDefaultDirectOutput = data => {
  if (data == null) return undefined
  if (Array.isArray(data)) return data
  if (typeof data !== 'object') return data

  if (data.output !== undefined) return data.output
  if (data.result !== undefined) return data.result
  if (data.data !== undefined) return data.data

  return undefined
}

/**
 * Custom hook for polling task status
 * @param {Object} options Configuration options
 * @param {number} options.pollInterval Polling interval in ms (default: 2000)
 * @param {boolean} options.continuePollingOnError Whether to keep polling after an API error (default: true)
 */
export const useTaskPoller = ({
  pollInterval = DEFAULT_POLL_INTERVAL,
  continuePollingOnError = true,
  getStatus = getTaskStatus
} = {}) => {
  const [isPolling, setIsPolling] = useState(false)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState(null)
  const [message, setMessage] = useState('')
  const [activeTaskId, setActiveTaskId] = useState(null)
  const stopPollingRef = useRef(false)
  const activePollRef = useRef({ token: null, taskId: null })
  const pollTimeoutsRef = useRef(new Set())
  const cancelActivePollRef = useRef(null)

  const clearPollTimeouts = useCallback(() => {
    pollTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId)
    })
    pollTimeoutsRef.current.clear()
  }, [])

  const stopPolling = useCallback(() => {
    stopPollingRef.current = true
    clearPollTimeouts()
    if (cancelActivePollRef.current) {
      cancelActivePollRef.current()
      cancelActivePollRef.current = null
      return
    }
    setIsPolling(false)
    setStatus('stopped')
    setActiveTaskId(null)
  }, [clearPollTimeouts])

  useEffect(() => () => clearPollTimeouts(), [clearPollTimeouts])

  const startPolling = useCallback(
    async taskId => {
      if (!taskId) {
        throw new Error('Task ID is required for polling')
      }

      stopPollingRef.current = false
      clearPollTimeouts()
      if (cancelActivePollRef.current) {
        cancelActivePollRef.current()
        cancelActivePollRef.current = null
      }

      const pollToken = Symbol(String(taskId))
      activePollRef.current = { token: pollToken, taskId }

      const isCurrentPoll = () =>
        activePollRef.current.token === pollToken &&
        activePollRef.current.taskId === taskId

      setIsPolling(true)
      setProgress(0)
      setStatus('pending')
      setMessage('')
      setActiveTaskId(taskId)

      return new Promise((resolve, reject) => {
        const resolveCancelled = () => {
          if (isCurrentPoll()) {
            setIsPolling(false)
            setStatus('stopped')
            setActiveTaskId(null)
          }
          cancelActivePollRef.current = null
          resolve({ status: 'cancelled' })
        }

        cancelActivePollRef.current = resolveCancelled

        const schedulePoll = () => {
          const timeoutId = setTimeout(() => {
            pollTimeoutsRef.current.delete(timeoutId)
            poll()
          }, pollInterval)

          pollTimeoutsRef.current.add(timeoutId)
        }

        const poll = async () => {
          if (stopPollingRef.current) {
            resolveCancelled()
            return
          }

          if (!isCurrentPoll()) {
            return
          }

          try {
            const response = await getStatus(taskId)
            if (stopPollingRef.current) {
              resolveCancelled()
              return
            }

            if (!isCurrentPoll()) {
              return
            }

            const data = response?.data

            if (!data) {
              console.warn('Empty response from task status API')
              schedulePoll()
              return
            }

            const currentStatus = data.status
            const normalizedStatus = String(currentStatus || '').toLowerCase()
            const currentProgress = data.progress
            const currentMessage = data.message || ''

            if (isCurrentPoll()) {
              setStatus(currentStatus)
              setMessage(currentMessage)
            }

            if (currentProgress) {
              // Handle both distinct formats if necessary, often API returns number or "50%"
              const progressNum =
                typeof currentProgress === 'string'
                  ? parseInt(currentProgress.replace('%', ''))
                  : currentProgress
              if (isCurrentPoll()) {
                setProgress(progressNum)
              }
            }

            if (SUCCESS_STATUSES.includes(normalizedStatus)) {
              if (isCurrentPoll()) {
                setIsPolling(false)
                setStatus('success')
                setProgress(100)
                setActiveTaskId(null)
              }
              cancelActivePollRef.current = null
              resolve(data) // Return full data object (usually has 'output' field)
            } else if (
              normalizedStatus === 'failed' ||
              normalizedStatus === 'error'
            ) {
              if (isCurrentPoll()) {
                setIsPolling(false)
                setStatus('failed')
                setActiveTaskId(null)
              }
              cancelActivePollRef.current = null
              reject(
                new Error(
                  data.error_msg ||
                    data.error ||
                    data.message ||
                    'Task processing failed'
                )
              )
            } else {
              // Still pending or processing
              schedulePoll()
            }
          } catch (error) {
            console.error('Error polling task status:', error)
            // Decide if we want to retry or fail. Usually retry on network error.
            // For now, let's retry unless it's a 4xx error that's not 429
            if (continuePollingOnError) {
              if (isCurrentPoll()) {
                schedulePoll()
              }
            } else {
              if (isCurrentPoll()) {
                setIsPolling(false)
                setStatus('failed')
                setActiveTaskId(null)
              }
              cancelActivePollRef.current = null
              reject(error)
            }
          }
        }

        poll()
      })
    },
    [pollInterval, continuePollingOnError, getStatus, clearPollTimeouts]
  )

  /**
   * Resolve API responses that may return either:
   * 1) A task id requiring polling, or
   * 2) Direct output data immediately.
   */
  const resolveTaskData = useCallback(
    async (
      responseOrData,
      {
        getTaskId = extractDefaultTaskId,
        getDirectOutput = extractDefaultDirectOutput,
        isDirectResponse
      } = {}
    ) => {
      const data =
        responseOrData && responseOrData.data !== undefined
          ? responseOrData.data
          : responseOrData

      if (data == null) {
        throw new Error('Empty API response')
      }

      const taskId =
        typeof getTaskId === 'function'
          ? getTaskId(data)
          : extractDefaultTaskId(data)

      if (taskId) {
        return startPolling(taskId)
      }

      const output =
        typeof getDirectOutput === 'function'
          ? getDirectOutput(data)
          : extractDefaultDirectOutput(data)

      const normalizedStatus =
        typeof data?.status === 'string' ? data.status.toLowerCase() : ''

      const defaultDirectCheck =
        SUCCESS_STATUSES.includes(normalizedStatus) ||
        data?.success === true ||
        output !== undefined

      const isDirectSuccess =
        typeof isDirectResponse === 'function'
          ? isDirectResponse(data, output)
          : defaultDirectCheck

      if (!isDirectSuccess) {
        throw new Error(
          'Missing task ID and response was not recognized as a direct success payload'
        )
      }

      const baseData =
        data && typeof data === 'object' && !Array.isArray(data) ? data : {}

      return {
        ...baseData,
        status: baseData.status || 'success',
        output,
        raw: data
      }
    },
    [startPolling]
  )

  return {
    isPolling,
    progress,
    status,
    message,
    activeTaskId,
    startPolling,
    stopPolling,
    resolveTaskData
  }
}

export default useTaskPoller
